import { useMemo } from 'react';
import { Box } from '@mui/material';
import { styled } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import { QButton, QModal, QModalActions, QModalContent, QModalTitle, QTypography } from '@kw/quokka-ui';

export interface AssociatedRule {
  name: string;
  type: string;
  ruleId: string;
  didTrigger: boolean;
  appIorResultsMap: Record<string, string[] | string>;
}

export interface TriggeredAction {
  name: string;
  description: string;
}

export interface PolicyResult {
  id: string;
  didTrigger: boolean;
  policyName: string;
  associatedRules: AssociatedRule[];
  actionsTriggered: TriggeredAction[];
  policyDescription: string;
}

interface PolicyViolationsModalProps {
  open: boolean;
  policy: PolicyResult | null;
  onClose: () => void;
  ruleOverviewRuleIds?: Set<string>;
  permissionOverviewRuleIds?: Set<string>;
  behavioralRuleIds?: Set<string>;
}

export function PolicyViolationsModal({
  open,
  policy,
  onClose,
  ruleOverviewRuleIds = new Set<string>(),
  permissionOverviewRuleIds = new Set<string>(),
  behavioralRuleIds = new Set<string>(),
}: PolicyViolationsModalProps) {
  const policyRuleIds = useMemo(() => {
    if (!policy) return [] as string[];
    const ids: string[] = [];
    for (const rule of policy.associatedRules ?? []) {
      for (const value of Object.values(rule.appIorResultsMap ?? {})) {
        if (Array.isArray(value)) {
          ids.push(...value.filter(Boolean));
        } else if (value) {
          ids.push(value);
        }
      }
    }
    return Array.from(new Set(ids));
  }, [policy]);

  const ruleOverviewFindingsCount = useMemo(
    () => policyRuleIds.filter(id => ruleOverviewRuleIds.has(id)).length,
    [policyRuleIds, ruleOverviewRuleIds],
  );

  const permissionOverviewFindingsCount = useMemo(
    () => policyRuleIds.filter(id => permissionOverviewRuleIds.has(id)).length,
    [policyRuleIds, permissionOverviewRuleIds],
  );

  const behavioralFindingsCount = useMemo(
    () => policyRuleIds.filter(id => behavioralRuleIds.has(id)).length,
    [policyRuleIds, behavioralRuleIds],
  );

  if (!policy) return null;

  const hasFindings = ruleOverviewFindingsCount > 0 || permissionOverviewFindingsCount > 0 || behavioralFindingsCount > 0;

  return (
    <QModal
      open={open}
      onClose={onClose}
      aria-labelledby="policy-violations-modal-title"
      title={
        <QModalTitle onClose={onClose} closeIcon={<CloseIcon />}>
          <QTypography variant="h3Medium" id="policy-violations-modal-title">
            Policy Violation
          </QTypography>
        </QModalTitle>
      }
      content={
        <StyledModalContent>
          <Section>
            <LabelHeading>
              <img width={16} src={'/icons/shield-checked.svg'} alt="Policy" />
              Policy
            </LabelHeading>
            <QTypography variant="body2">
              <UnderlinedSpan>{policy.policyName}</UnderlinedSpan>: {policy.policyDescription || 'No description provided.'}
            </QTypography>
          </Section>

          {policy.associatedRules?.length ? (
            <Section>
              <LabelHeading>
                <img width={16} src={'/icons/clipboard.svg'} alt="Policy Rule(s)" />
                <span>Policy Rule(s)</span>
              </LabelHeading>
              {policy.associatedRules.map(rule => (
                <QTypography key={rule.ruleId} variant="body2">
                  {rule.name}
                </QTypography>
              ))}
            </Section>
          ) : null}

          {hasFindings && (
            <>
              <Section>
                <LabelHeading>
                  <img width={16} src={'/icons/binoculars.svg'} alt="Findings" />
                  <span>Findings</span>
                </LabelHeading>
                {ruleOverviewFindingsCount > 0 && (
                  <QTypography variant="body2">Overview section under Rules ({ruleOverviewFindingsCount})</QTypography>
                )}
                {permissionOverviewFindingsCount > 0 && (
                  <QTypography variant="body2">Overview section under Permissions ({permissionOverviewFindingsCount})</QTypography>
                )}
                {behavioralFindingsCount > 0 && <QTypography variant="body2">Behaviors section ({behavioralFindingsCount})</QTypography>}
              </Section>
              <QTypography variant="body2">
                Use the Policy Violations filter in these sections to view findings for this violation.
              </QTypography>
            </>
          )}
        </StyledModalContent>
      }
      actions={
        <QModalActions>
          <QButton onClick={onClose} variant="filled">
            Got it
          </QButton>
        </QModalActions>
      }
    />
  );
}

const StyledModalContent = styled(QModalContent)(() => ({
  padding: 16,
  display: 'flex',
  flexDirection: 'column',
  gap: 16,
}));

const Section = styled(Box)(() => ({
  display: 'flex',
  flexDirection: 'column',
  gap: 4,
}));

const LabelHeading = styled(QTypography)(() => ({
  fontSize: 14,
  fontWeight: 700,
  display: 'inline-flex',
  alignItems: 'center',
  gap: 3,
}));

const UnderlinedSpan = styled('span')(() => ({
  textDecoration: 'underline',
}));
